<template>
  <div class="d-flex fill-height position-relative" style="height: 100%; overflow: hidden">
    <!-- Middle Panel: Calendar -->
    <div
      class="flex-grow-1 d-flex flex-column pa-4 grey lighten-5 border-xy"
      style="height: 100%; overflow: hidden"
    >
      <div class="d-flex align-center justify-space-between mb-4">
        <v-btn icon meduim @click="$refs.calendar.prev()" color="grey darken-2">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <div class="text-h6 font-weight-bold grey--text text--darken-3">{{ calendarTitle }}</div>
        <v-btn icon meduim @click="$refs.calendar.next()" color="grey darken-2">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <v-sheet class="flex-grow-1 rounded-lg overflow-hidden elevation-1" min-height="0">
        <v-calendar
          ref="calendar"
          v-model="focus"
          :events="events"
          :event-color="getEventColor"
          :type="calendarType"
          color="primary"
          @click:date="viewDay"
          @change="updateRange"
          :now="today"
        >
          <template v-slot:day="{ date }">
            <div
              class="fill-height d-flex flex-column pa-1 cursor-pointer"
              @click="viewDay({ date: date })"
              style="font-size: 10px; line-height: 1.2"
            >
              <div v-if="hasAttendanceData(date)" class="mt-1">
                <div>
                  Clock In: <span class="font-weight-bold">{{ attendanceMap[date].checkIn }}</span>
                </div>
                <div>
                  Clock Out:
                  <span class="font-weight-bold">{{ attendanceMap[date].checkOut }}</span>
                </div>
                <div>
                  Working Hrs:
                  <span class="font-weight-bold">{{ attendanceMap[date].workingHours }}</span>
                </div>
              </div>
            </div>
          </template>
        </v-calendar>
      </v-sheet>
    </div>

    <!-- Right Panel: Task Activity -->
    <v-card
      class="elevation-1 d-flex flex-column"
      :style="{
        width: rightSidebarCollapsed ? '48px' : '450px',
        height: '100%',
        overflow: 'hidden',
        transition: 'width 0.22s ease',
        minWidth: 0
      }"
      tile
    >
      <!-- Mini collapsed strip (shown when collapsed) -->
      <div
        v-if="rightSidebarCollapsed"
        class="d-flex flex-column align-center justify-start flex-grow-1 pt-3 cursor-pointer"
        @click="rightSidebarCollapsed = false"
        style="overflow: hidden"
        title="Open Activity Panel"
      >
        <v-icon size="22" color="grey lighten-1" class="mb-2">mdi-calendar-check</v-icon>
        <div
          style="
            writing-mode: vertical-rl;
            text-orientation: mixed;
            transform: rotate(180deg);
            font-size: 10px;
            font-weight: 700;
            color: #475569;
            letter-spacing: 0.05em;
            text-transform: uppercase;
            white-space: nowrap;
          "
        >
          Activity
        </div>
      </div>

      <!-- Full sidebar content (hidden when collapsed) -->
      <template v-if="!rightSidebarCollapsed">
        <div class="subtitle-1 font-weight-bold pa-4 pb-2 d-flex align-center justify-space-between">
          <div class="d-flex align-center overflow-hidden">
            <v-icon left color="primary">mdi-calendar-check</v-icon>
            <span class="text-truncate">Activity: {{ formattedSelectedDate }}</span>
          </div>
          <v-btn icon small @click="rightSidebarCollapsed = true" title="Close Activity Panel">
            <v-icon small color="grey darken-1">mdi-close</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>

        <div class="overflow-y-auto flex-grow-1 pa-4 grey lighten-5">
          <div v-if="loadingTasks" class="d-flex justify-center align-center fill-height">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <template v-else>
            <div v-if="tasks.length > 0">
              <v-card v-for="(task, i) in tasks" :key="i" class="mb-3 rounded-lg" outlined>
                <v-card-title
                  class="subtitle-2 white--text py-2 px-3 d-flex justify-space-between align-center"
                  :class="getTaskHeaderColor(task.status)"
                >
                  <div class="d-flex align-center overflow-hidden">
                    <v-icon small color="white" class="mr-2">
                      {{ getTaskIcon(task.type) }}
                    </v-icon>
                    <span class="text-truncate">{{ task.title }}</span>
                  </div>
                  <!-- <v-icon small color="white">mdi-checkbox-marked-circle-outline</v-icon> -->
                  <v-chip x-small class="white black--text font-weight-bold" label>
                    {{ task.status }}
                  </v-chip>
                </v-card-title>

                <v-card-text class="pa-3">
                  <div class="d-flex justify-space-between mb-2">
                    <div class="caption grey--text d-flex align-center">
                      <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>
                      <span class="font-weight-medium">{{ task.time }}</span>
                    </div>
                    <div
                      v-if="task.caseNo"
                      class="caption blue-grey--text text--lighten-1 font-weight-bold"
                    >
                      {{ task.caseNo }}
                    </div>
                  </div>

                  <div v-if="task.customerName" class="d-flex align-center mb-2">
                    <v-avatar size="24" color="grey lighten-3" class="mr-2">
                      <span class="text-caption font-weight-bold blue--text">{{
                        task.customerName.charAt(0)
                      }}</span>
                    </v-avatar>
                    <div>
                      <div class="caption grey--text text--darken-1">Customer</div>
                      <div class="text-caption font-weight-bold black--text lh-1">
                        {{ task.customerName }}
                      </div>
                    </div>
                  </div>
                  <div v-if="task.address" class="mt-2 pt-2 border-top">
                    <div class="caption d-flex align-start text-no-wrap">
                      <v-icon x-small class="mr-1 mt-0.5 red--text">mdi-map-marker</v-icon>
                      <span
                        class="text-caption grey--text text--darken-2 text-wrap"
                        style="line-height: 1.2"
                        >{{ task.address }}</span
                      >
                    </div>
                  </div>

                  <v-divider
                    class="my-2"
                    v-if="task.questions && task.questions.length"
                  ></v-divider>

                  <div v-if="task.questions && task.questions.length">
                    <div class="caption font-weight-bold grey--text text--darken-2 mb-1">
                      Form Data
                    </div>
                    <div v-for="(qa, j) in task.questions" :key="j" class="mb-1">
                      <div class="font-weight-medium text-caption black--text">
                        {{ j + 1 }}. {{ qa.question }}
                      </div>
                      <div v-if="qa.mediaUrls && qa.mediaUrls.length > 0" class="d-flex flex-wrap">
                        <div v-for="(url, k) in qa.mediaUrls" :key="k" class="mr-2 mb-1">
                          <template
                            v-if="
                              [
                                'CLICK_PICTURES',
                                'PHOTO',
                                'CHECK_IN_PHOTO',
                                'CHECK_OUT_PHOTO',
                                'LOCAL_ATTACHMENTS'
                              ].includes(qa.type)
                            "
                          >
                            <v-img
                              :src="url"
                              max-width="100"
                              max-height="100"
                              class="rounded elevation-1 cursor-pointer"
                              @click="viewImage(url)"
                            ></v-img>
                          </template>
                          <template v-else-if="qa.type === 'VIDEO_CAPTURE'">
                            <video
                              :src="url"
                              controls
                              style="max-width: 200px; max-height: 150px"
                              class="rounded elevation-1"
                            ></video>
                          </template>
                          <template v-else>
                            <a
                              :href="url"
                              target="_blank"
                              class="text-caption text-decoration-none blue--text"
                            >
                              View {{ qa.type }}
                              <v-icon x-small color="blue">mdi-open-in-new</v-icon>
                            </a>
                          </template>
                        </div>
                      </div>
                      <div
                        v-else
                        class="text-caption grey--text text--darken-2 pl-2 border-left-primary"
                        style="white-space: pre-wrap"
                      >
                        {{ qa.answer || '-' }}
                      </div>
                    </div>
                  </div>

                  <div v-if="task.selfie" class="mt-2">
                    <div class="caption font-weight-bold grey--text text--darken-2 mb-1">
                      Selfie
                    </div>
                    <v-img
                      :src="task.selfie"
                      max-width="100"
                      max-height="100"
                      class="rounded elevation-1 cursor-pointer"
                      @click="viewImage(task.selfie)"
                    ></v-img>
                  </div>
                </v-card-text>
              </v-card>
            </div>

            <div
              v-else
              class="d-flex flex-column align-center justify-center fill-height text-center grey--text"
            >
              <v-img
                :src="require('@/assets/img/notfound.jpg')"
                max-width="150"
                class="mb-4 opacity-50"
                contain
              >
                <template v-slot:placeholder>
                  <v-icon size="64" color="grey lighten-2">mdi-calendar-blank</v-icon>
                </template>
              </v-img>
              <div class="subtitle-1 font-weight-medium">No Activity Found</div>
              <div class="caption">No tasks recorded for this date.</div>
            </div>
          </template>
        </div>
      </template>
    </v-card>

    <!-- Collapse toggle button overlaying the left border of the right panel -->
    <div
      style="
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 5;
        pointer-events: none;
        display: flex;
        align-items: center;
      "
      :style="{ right: rightSidebarCollapsed ? '48px' : '450px', transition: 'right 0.22s ease' }"
    >
      <button
        @click="rightSidebarCollapsed = !rightSidebarCollapsed"
        style="
          pointer-events: all;
          width: 18px;
          height: 48px;
          background: #cbd5e1;
          border: 1px solid #94a3b8;
          border-right: none;
          border-radius: 6px 0 0 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: -2px 0 6px rgba(0, 0, 0, 0.12);
          padding: 0;
          outline: none;
          transition: background 0.15s;
        "
        @mouseover="$event.currentTarget.style.background = '#94a3b8'"
        @mouseleave="$event.currentTarget.style.background = '#cbd5e1'"
      >
        <v-icon size="13" color="grey darken-3">
          {{ rightSidebarCollapsed ? 'mdi-chevron-left' : 'mdi-chevron-right' }}
        </v-icon>
      </button>
    </div>
  </div>
</template>

<script>
// import UserAvatar from '@/components/UserAvatar.vue'
import EmployeeInfo from '@/components/EmployeeInfo.vue'
import { debounce } from '@/utils/common'

export default {
  name: 'TeamReviewCalendar',
  components: {
    // UserAvatar,
    // EmployeeInfo
  },
  props: {
    employees: {
      type: Array,
      default: () => []
    },
    orgConfig: {
      type: Object,
      default: () => ({})
    },
    userId: {
      type: String,
      default: null
    }
  },
  inject: {
    teamReviewContext: {
      default: null
    }
  },
  data: () => ({
    search: '',
    selectedRole: null,
    selectedUser: null,
    focus: '',
    selectedDate: null,
    events: [],
    attendanceMap: {},
    tasks: [],
    loadingTasks: false,
    calendarType: 'month',
    today: new Date().toISOString().substr(0, 10),
    calendarTitle: '',
    rightSidebarCollapsed: true
  }),
  computed: {
    formattedSelectedDate() {
      return this.selectedDate ? this.$dayjs(this.selectedDate).format('DD MMM, YYYY') : ''
    }
  },
  watch: {
    userId: {
      handler(val) {
        if (val) {
          const user = this.employees.find((u) => u._id === val || u.userId?._id === val)
          if (user) {
            this.selectedUser = user
            this.fetchAttendance()
            if (this.selectedDate) {
              this.fetchTasks(this.selectedDate)
            }
          }
        }
      },
      immediate: true
    },
    employees: {
      handler(val) {
        if (this.userId) {
          const user = val.find((u) => u._id === this.userId || u.userId?._id === this.userId)
          if (user) {
            this.selectedUser = user
          }
        }
      }
    }
  },
  methods: {
    fetchAttendance: debounce(async function () {
      if (!this.selectedUser || !this.focus) return

      const start = this.$dayjs(this.focus).startOf('month').format('YYYY-MM-DD')
      const end = this.$dayjs(this.focus).endOf('month').format('YYYY-MM-DD')

      try {
        const { data } = await this.$axios.get('/user/v2/attendance', {
          params: {
            userIds: this.selectedUser._id || this.selectedUser.userId?._id,
            start,
            end
          }
        })

        if (data && data.employeeAttendance) {
          this.events = data.employeeAttendance.map((att) => ({
            name: this.formatStatus(att.attendanceStatus),
            start: this.$dayjs(att.attendanceDate).format('YYYY-MM-DD'),
            color: this.getStatusColor(att.attendanceStatus),
            details: att,
            timed: false
          }))

          this.attendanceMap = {}
          data.employeeAttendance.forEach((att) => {
            const date = this.$dayjs(att.attendanceDate).format('YYYY-MM-DD')

            const checkInEntry = att.checkInOutData?.find((c) => c.status === 'CHECKED_IN')
            const checkOutEntry = att.checkInOutData?.find((c) => c.status === 'CHECKED_OUT')

            this.attendanceMap[date] = {
              checkIn: checkInEntry ? this.$dayjs(checkInEntry.time).format('HH:mm') : '-',
              checkOut: checkOutEntry ? this.$dayjs(checkOutEntry.time).format('HH:mm') : '-',
              workingHours: att.actualWorkingHrs || '-'
            }
          })
        } else {
          this.events = []
        }
      } catch (e) {
        console.error('Error fetching attendance:', e)
        this.events = []
      }
    }, 100),
    formatStatus(status) {
      if (!status) return '-'
      return status
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },
    getStatusColor(status) {
      const colors = {
        PRESENT: 'success',
        ABSENT: 'error',
        LEAVE: 'warning',
        HOLIDAY: 'info',
        WEEK_OFF: 'grey lighten-1',
        HALFDAY: 'amber'
      }
      return colors[status] || 'primary'
    },
    viewDay({ date }) {
      this.selectedDate = date
      this.rightSidebarCollapsed = false
      this.fetchTasks(date)
    },
    getTaskHeaderColor(status) {
      const statusMap = {
        COMPLETED: 'success',
        PENDING: 'warning',
        MISSED: 'error',
        IN_PROGRESS: 'info'
      }
      return statusMap[status] || 'primary'
    },
    getTaskIcon(type) {
      const iconMap = {
        VISIT: 'mdi-map-marker-check',
        COLLECTION: 'mdi-cash-multiple',
        TASK: 'mdi-clipboard-check'
      }
      return iconMap[type] || 'mdi-checkbox-marked-circle-outline'
    },
    viewImage(url) {
      if (url) window.open(url, '_blank')
    },
    async fetchTasks(date) {
      if (!this.selectedUser) return

      this.loadingTasks = true
      this.tasks = []

      try {
        const userId = this.selectedUser._id || this.selectedUser.userId?._id
        const response = await this.$axios.get(`/location-time-series/${userId}`, {
          params: { date }
        })

        const timeSeries = response.data?.data?.data || []

        // Extract Task IDs and Map relevant points
        const taskIds = new Set()
        const relevantPoints = {} // Map tid -> point

        timeSeries.forEach((point) => {
          const act = point.activity
          if (act && (act.caseTaskDetails || act.taskDetails)) {
            const tid = act.caseTaskDetails || act.taskDetails
            if (!taskIds.has(tid)) {
              taskIds.add(tid)
              relevantPoints[tid] = point
            }
          }
        })

        if (taskIds.size === 0) {
          this.loadingTasks = false
          return
        }

        // Fetch Details for each task
        const taskPromises = Array.from(taskIds).map((tid) =>
          this.$axios.get(`/collection/case/task/${tid}`)
        )

        const taskResponses = await Promise.allSettled(taskPromises)

        this.tasks = taskResponses
          .filter((r) => r.status === 'fulfilled' && r.value.data?.success)
          .map((r) => {
            const t = r.value.data.task
            const originalPoint = relevantPoints[t._id]

            // Normalize Questions
            let questions = []
            const mapQA = (arr) =>
              arr.map((i) => {
                const answer = i.userResponse || i.answer || i.value || '-'
                let mediaUrls = []
                const isMedia = [
                  'PHOTO',
                  'CLICK_PICTURES',
                  'VIDEO_CAPTURE',
                  'LOCAL_ATTACHMENTS',
                  'AUDIO',
                  'CHECK_IN_PHOTO',
                  'CHECK_OUT_PHOTO'
                ].includes(i.questionType)

                let paths = []

                if (isMedia && answer !== '-') {
                  if (Array.isArray(answer)) {
                    paths = [...answer]
                  } else if (typeof answer === 'string') {
                    if (answer.trim().startsWith('[') && answer.trim().endsWith(']')) {
                      try {
                        const parsed = JSON.parse(answer)
                        if (Array.isArray(parsed)) {
                          paths = [...paths, ...parsed]
                        } else {
                          paths.push(answer)
                        }
                      } catch (e) {
                        paths.push(answer)
                      }
                    } else {
                      paths.push(answer)
                    }
                  }
                }

                if (i.attachments && Array.isArray(i.attachments)) {
                  paths = [...paths, ...i.attachments]
                }

                if (i.localAttachments && Array.isArray(i.localAttachments)) {
                  paths = [...paths, ...i.localAttachments]
                }

                if (paths.length > 0) {
                  const baseUrl = process.env.VUE_APP_S3_BASE_URL
                  mediaUrls = [...new Set(paths)].map((a) =>
                    a.startsWith('http') ? a : `${baseUrl}${a}`
                  )
                }

                return {
                  question: i.question || i.label || '-',
                  answer:
                    (Array.isArray(answer) && answer.length === 0) || answer === '[]'
                      ? '-'
                      : answer,
                  type: i.questionType,
                  mediaUrls
                }
              })

            if (t.meetingDetails?.questions && t.meetingDetails.questions.length > 0) {
              questions = mapQA(t.meetingDetails.questions)
            } else if (
              t.meetingDetails?.questionnaire &&
              t.meetingDetails.questionnaire.length > 0
            ) {
              questions = mapQA(t.meetingDetails.questionnaire)
            } else if (t.dynamicForm && t.dynamicForm.length > 0) {
              questions = mapQA(t.dynamicForm)
            }

            // Normalize Selfie
            let selfie = null
            if (t.additionalInfo?.selfie?.doc_physical_path) {
              selfie = `${process.env.VUE_APP_S3_BASE_URL}${t.additionalInfo.selfie.doc_physical_path}`
            }

            return {
              id: t._id,
              type: t.taskType || 'TASK',
              title: t.taskTitle || t.case?.caseNo || 'Task',
              caseNo: t.case?.caseNo,
              customerName: t.case?.customer?.customerFullName || t.customerName,
              time: originalPoint
                ? this.$dayjs(originalPoint.createdAt || originalPoint.timestamp).format('hh:mm A')
                : '-',
              status: t.taskStatus,
              address: originalPoint?.location?.address || originalPoint?.address || t.address,
              meetingDetails: t.meetingDetails
                ? {
                    outcome: t.meetingDetails.outcome || t.meetingDetails.meetingStatus,
                    notes: t.meetingDetails.notes || t.meetingDetails.meetingNotes
                  }
                : null,
              questions: questions,
              selfie: selfie
            }
          })
      } catch (e) {
        console.error('Error fetching tasks:', e)
      } finally {
        this.loadingTasks = false
      }
    },
    updateRange({ start, end }) {
      this.focus = start.date
      this.calendarTitle = this.$dayjs(this.focus).format('MMMM YYYY')
      // Fetch attendance if user is selected
      if (this.selectedUser) {
        this.fetchAttendance()
      }
    },
    getEventColor(event) {
      return event.color
    },
    hasAttendanceData(date) {
      const att = this.attendanceMap[date]
      if (!att) return false
      return att.checkIn !== '-' || att.checkOut !== '-' || att.workingHours !== '-'
    }
  },
  mounted() {
    this.focus = this.$dayjs().format('YYYY-MM-DD')
    this.selectedDate = this.$dayjs().format('YYYY-MM-DD')
    // Ensure initial title is set
    this.calendarTitle = this.$dayjs(this.focus).format('MMMM YYYY')
  }
}
</script>

<style scoped>
.border-xy {
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
}
.opacity-50 {
  opacity: 0.5;
}
.border-left-primary {
  border-left: 2px solid #2196f3;
}
.lh-1 {
  line-height: 1;
}
::v-deep .v-calendar-weekly__head-weekday {
  font-weight: bold !important;
  color: #333;
}
::v-deep .v-calendar-weekly__day {
  min-height: 40px;
}
</style>
